import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Facebook, Instagram, Menu, X } from "lucide-react";

const LINKS = [
  { to: "/", label: "Accueil", testId: "nav-link-accueil" },
  { to: "/soins", label: "Soins", testId: "nav-link-soins" },
  { to: "/formations", label: "Formations", testId: "nav-link-formations" },
  { to: "/constellations", label: "Constellations", testId: "nav-link-constellations" },
  { to: "/ateliers", label: "Ateliers", testId: "nav-link-ateliers" },
  { to: "/sejour-initiatique", label: "Séjour initiatique", testId: "nav-link-sejour" },
  { to: "/blog", label: "Journal", testId: "nav-link-blog" },
  { to: "/guides", label: "Guides", testId: "nav-link-guides" },
  { to: "/contact", label: "Contact", testId: "nav-link-contact" },
];

export default function Navbar() {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);

  if (pathname === "/trek-sahara" || pathname === "/formation-chi-nei-tsang" || pathname === "/formation-reflexologie-plantaire") return null;

  return (
    <header data-testid="header-navigation-bar" className="fixed top-0 inset-x-0 z-50 backdrop-blur-md bg-beige-100/80 border-b border-olive-border/60">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 h-20 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/" data-testid="nav-brand-logo" className="flex items-center gap-3 group" onClick={() => setOpen(false)}>
            <img src="/images/logo-icon.png" alt="Logo Julie Zittel" className="h-9 w-9 object-contain transition-transform duration-500 group-hover:scale-110" />
            <span className="font-serif text-2xl tracking-tight text-ink whitespace-nowrap">Julie Zittel</span>
          </Link>
          <div className="flex items-center gap-2" data-testid="nav-social-links">
            <a
              href="https://www.instagram.com/juliezittel/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-instagram"
              aria-label="Instagram de Julie Zittel"
              className="flex h-8 w-8 items-center justify-center rounded-full border border-olive-border text-olive transition-all duration-300 hover:bg-olive hover:text-beige-100 hover:-translate-y-0.5"
            >
              <Instagram className="w-4 h-4" strokeWidth={1.5} />
            </a>
            <a
              href="https://www.facebook.com/julieosteotheotherapeute"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-facebook"
              aria-label="Facebook de Julie Zittel"
              className="flex h-8 w-8 items-center justify-center rounded-full border border-olive-border text-olive transition-all duration-300 hover:bg-olive hover:text-beige-100 hover:-translate-y-0.5"
            >
              <Facebook className="w-4 h-4" strokeWidth={1.5} />
            </a>
          </div>
        </div>

        <nav className="hidden xl:flex items-center gap-5 xl:gap-6">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={l.testId}
              className={({ isActive }) =>
                `text-[13px] tracking-wide whitespace-nowrap transition-colors duration-300 hover:text-olive ${isActive ? "text-olive font-medium" : "text-sage"}`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <Link
            to="/contact"
            data-testid="nav-cta-booking"
            className="ml-1 inline-flex items-center whitespace-nowrap rounded-full bg-olive px-5 py-2.5 text-[13px] font-medium text-beige-100 transition-all duration-300 hover:bg-olive-dark hover:-translate-y-0.5"
          >
            Prendre rendez-vous
          </Link>
        </nav>

        <button data-testid="nav-mobile-toggle" className="xl:hidden text-ink" onClick={() => setOpen(!open)} aria-label="Menu">
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <nav data-testid="nav-mobile-menu" className="xl:hidden border-t border-olive-border/60 bg-beige-100/95 backdrop-blur-md px-6 py-6 flex flex-col gap-4">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`${l.testId}-mobile`}
              onClick={() => setOpen(false)}
              className={({ isActive }) => `font-serif text-2xl ${isActive ? "text-olive" : "text-ink"}`}
            >
              {l.label}
            </NavLink>
          ))}
          <Link
            to="/contact"
            data-testid="nav-cta-booking-mobile"
            onClick={() => setOpen(false)}
            className="mt-2 inline-flex w-fit items-center rounded-full bg-olive px-6 py-3 text-sm font-medium text-beige-100"
          >
            Prendre rendez-vous
          </Link>
        </nav>
      )}
    </header>
  );
}
